import { useEffect, useRef, useState } from 'react';
import { Bell, Check, RefreshCw } from 'lucide-react';
import { useClickOutside } from '../../hooks/useClickOutside';

/**
 * Live in-app notifications bell.
 *
 * Polls `/api/notifications/InAppNotification` every minute (or whenever
 * the dropdown is opened). Renders unread count as a red pip on the
 * bell, lists the most-recent 50 entries, and lets the user mark a row
 * read or wipe the queue with "Mark all read".
 *
 * `@kss/ui` has no hard dep on @kss/api so we resolve the apiClient
 * lazily through `window.__kssApi` (set by `configureApi`) — falling
 * back to silence when the API isn't wired (UI showcase / SSR).
 */

interface NotificationRow {
  id?: number | string;
  inAppNotificationId?: string;
  title?: string;
  message?: string;
  body?: string;
  isRead?: boolean | number;
  read?: boolean;
  readAt?: string | null;
  link?: string;
  url?: string;
  createdAt?: string;
  indate?: string;
  severity?: 'info' | 'success' | 'warning' | 'danger' | string;
}

const POLL_MS = 60 * 1000; // 1 minute

declare global {
  interface Window {
    __kssApi?: {
      get(path: string, config?: unknown): Promise<{ data: unknown }>;
      put(path: string, body: unknown): Promise<{ data: unknown }>;
      post(path: string, body: unknown): Promise<{ data: unknown }>;
      getToken(): string | null;
      baseURL(): string;
    };
  }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false));

  async function load() {
    if (!window.__kssApi) return;
    setLoading(true);
    setErr(null);
    try {
      const r = await window.__kssApi.get('/notifications/InAppNotification');
      const list = pickList<NotificationRow>(r.data);
      // Most recent first.
      list.sort((a, b) => Date.parse(timestamp(b)) - Date.parse(timestamp(a)));
      setRows(list.slice(0, 50));
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  // Poll every minute, but also subscribe to a server-sent-events stream
  // so a server-side push reaches the bell within milliseconds. Polling
  // is kept as a safety net for when the SSE connection drops.
  //
  // Auth note: EventSource's spec doesn't let callers set request
  // headers — so the JWT cannot be sent as `Authorization: Bearer ...`.
  // The backend accepts `?access_token=<jwt>` as a fallback (per the
  // §14.2 contract); we use that. The query token gets logged in IIS
  // request logs, which is a minor privacy trade-off we accept because
  // the alternative (cookies) would force us to maintain a separate
  // cookie-auth path purely for SSE.
  useEffect(() => {
    let cancelled = false;
    void load();
    const t = setInterval(() => { if (!cancelled) void load(); }, POLL_MS);
    let es: EventSource | null = null;
    if (typeof window !== 'undefined' && typeof EventSource !== 'undefined' && window.__kssApi) {
      try {
        const token = window.__kssApi.getToken();
        const base = window.__kssApi.baseURL();           // e.g. "/api" or full URL
        // Build URL: <base>/notifications/stream?access_token=<jwt>
        // The base may itself contain `/api`, in which case we strip
        // any trailing slash and append; the backend accepts either.
        const baseTrimmed = base.replace(/\/+$/, '');
        const url = token
          ? `${baseTrimmed}/notifications/stream?access_token=${encodeURIComponent(token)}`
          : `${baseTrimmed}/notifications/stream`;
        // withCredentials only matters when the API host echoes a
        // Set-Cookie; the BillBook API doesn't, but leaving it on is
        // free and keeps cross-origin scenarios consistent.
        es = new EventSource(url, { withCredentials: true });
        const onPush = () => { if (!cancelled) void load(); };
        // Backend emits typed `notification` events plus a one-shot
        // `hello` on connect; accept both, plus the default `message`
        // event for forward compatibility.
        es.addEventListener('message', onPush);
        es.addEventListener('notification', onPush);
        es.addEventListener('hello', () => {
          // Connection established. Trigger one load() so a notification
          // that arrived between the SSE-connect and the first poll
          // shows up immediately rather than a minute later.
          if (!cancelled) void load();
        });
        es.addEventListener('error', () => {
          // EventSource auto-reconnects on transient errors. If the
          // endpoint 404s repeatedly (backend not wired yet), the
          // polling fallback above still keeps the bell working.
        });
      } catch {
        es = null;
      }
    }
    return () => {
      cancelled = true;
      clearInterval(t);
      if (es) es.close();
    };
  }, []);

  useEffect(() => { if (open) void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open]);

  async function markRead(row: NotificationRow) {
    if (!window.__kssApi) return;
    if (isRead(row)) return;
    setRows((cur) =>
      cur.map((r) => (rowId(r) === rowId(row) ? { ...r, isRead: true, readAt: new Date().toISOString() } : r)),
    );
    try {
      await window.__kssApi.put('/notifications/InAppNotification', {
        ...row,
        isRead: true,
        readAt: new Date().toISOString(),
      });
    } catch {
      // Roll back optimistic update on failure.
      setRows((cur) =>
        cur.map((r) => (rowId(r) === rowId(row) ? { ...r, isRead: row.isRead, readAt: row.readAt } : r)),
      );
    }
  }

  async function markAllRead() {
    const unread = rows.filter((r) => !isRead(r));
    if (unread.length === 0) return;
    setRows((cur) => cur.map((r) => ({ ...r, isRead: true, readAt: new Date().toISOString() })));
    if (!window.__kssApi) return;
    for (const row of unread) {
      try {
        await window.__kssApi.put('/notifications/InAppNotification', {
          ...row,
          isRead: true,
          readAt: new Date().toISOString(),
        });
      } catch {
        /* swallow — optimistic UI already updated */
      }
    }
  }

  function openLink(row: NotificationRow) {
    void markRead(row);
    const href = row.link ?? row.url;
    if (typeof href === 'string' && href) {
      if (href.startsWith('/')) {
        window.location.assign(href);
      } else {
        window.open(href, '_blank', 'noopener,noreferrer');
      }
    }
    setOpen(false);
  }

  const unreadCount = rows.filter((r) => !isRead(r)).length;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-8 w-8 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-card-hover hover:text-content-primary"
        aria-label={`Notifications${unreadCount ? ` · ${unreadCount} unread` : ''}`}
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-surface-card shadow-lg">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h3 className="text-sm font-semibold text-content-primary">Notifications</h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void load()}
                title="Reload"
                aria-label="Reload notifications"
                className="text-content-muted hover:text-content-primary"
              >
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              </button>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                >
                  <Check size={11} /> Mark all read
                </button>
              )}
            </div>
          </div>
          {err && (
            <div className="border-b border-border bg-danger/5 px-4 py-2 text-[11px] text-danger">{err}</div>
          )}
          <div className="max-h-[360px] overflow-y-auto">
            {loading && rows.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-content-muted">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-content-muted">
                You&rsquo;re all caught up.
              </div>
            ) : (
              rows.map((r) => {
                const read = isRead(r);
                const sev = severityClass(r.severity);
                return (
                  <button
                    key={String(rowId(r))}
                    type="button"
                    onClick={() => openLink(r)}
                    className={`flex w-full items-start gap-3 border-b border-border/60 px-4 py-2.5 text-left transition-colors hover:bg-surface-card-hover last:border-0 ${
                      read ? 'opacity-70' : ''
                    }`}
                  >
                    <span
                      className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
                        read ? 'bg-content-muted/40' : sev
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${read ? 'text-content-secondary' : 'font-medium text-content-primary'}`}>
                        {String(r.title ?? '—')}
                      </p>
                      {(r.message || r.body) && (
                        <p className="mt-0.5 line-clamp-2 text-[11px] text-content-muted">
                          {String(r.message ?? r.body ?? '')}
                        </p>
                      )}
                      <p className="mt-0.5 text-[10px] text-content-muted">{relativeTime(timestamp(r))}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers ----------------------------------------------------------- */

function rowId(r: NotificationRow): string {
  return String(r.inAppNotificationId ?? r.id ?? '');
}

function isRead(r: NotificationRow): boolean {
  return r.isRead === true || r.isRead === 1 || r.read === true || !!r.readAt;
}

function timestamp(r: NotificationRow): string {
  return String(r.createdAt ?? r.indate ?? '');
}

function severityClass(s: NotificationRow['severity']): string {
  if (s === 'success') return 'bg-success';
  if (s === 'warning') return 'bg-warning';
  if (s === 'danger') return 'bg-danger';
  return 'bg-primary';
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const diffSec = Math.round((Date.now() - t) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.round(diffSec / 86400)}d ago`;
  return new Date(t).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function pickList<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
